import { BaseApi } from './base';
import type { Group } from './types';

export interface CreateGroupPayload {
  title: string;
}

export interface UpdateGroupPayload {
  title?: string;
}

export interface InviteMemberPayload {
  email: string;
  role: 'manager' | 'managed';
}

export interface AcceptInvitationPayload {
  code: string;
  /** IANA timezone string from the browser (e.g. "America/Chicago") */
  timezone?: string;
}

export interface RemoveMemberPayload {
  userId: string;
}

export interface ChangeRolePayload {
  userId: string;
  role: 'manager' | 'managed';
}

export class GroupsApi extends BaseApi {
  public async getCurrent(): Promise<Group | null> {
    return await this._request<Group | null>('/groups/current', {
      method: 'GET',
    });
  }

  public async getById(groupId: string): Promise<Group> {
    return await this._request<Group>(`/groups/${groupId}`, {
      method: 'GET',
    });
  }

  public async create(payload: CreateGroupPayload): Promise<Group> {
    return await this._request<Group>('/groups', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async update(
    groupId: string,
    payload: UpdateGroupPayload,
  ): Promise<Group> {
    return await this._request<Group>(`/groups/${groupId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async leave(): Promise<void> {
    await this._request<void>('/groups/leave', {
      method: 'POST',
    });
  }

  public async inviteMember(payload: InviteMemberPayload): Promise<void> {
    await this._request<void>('/groups/invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async acceptInvitation(
    payload: AcceptInvitationPayload,
  ): Promise<{ message: string; group: Group }> {
    return await this._request<{ message: string; group: Group }>(
      '/groups/accept-invitation',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );
  }

  public async removeMember(payload: RemoveMemberPayload): Promise<void> {
    await this._request<void>('/groups/remove-member', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async changeRole(payload: ChangeRolePayload): Promise<Group> {
    return await this._request<Group>('/groups/change-role', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }
}
